import {
  ChallengeContainer,
  ChallengeDescription,
  ChallengeField,
  ChallengeFieldError,
  ChallengeHeaderRow,
  ChallengeLoading,
  ChallengeRowTotalLabel,
  ChallengeSection,
  ChallengeSectionLabel,
  ChallengeTotalField,
  ChallengeTotalRow,
  FlexChallengeTitle,
} from '@/components/challenge/components';
import { ChallengeEntryRow } from '@/components/challenge/UserChallenge/ChallengeEntryRow';
import { getStatusAdornment } from '@/components/challenge/UserChallenge/getStatusAdornment';
import { DownloadIcon } from '@/components/icons';
import { useFieldSaveStatus } from '@/hooks/useFieldSaveStatus';
import { NumberFormatCustom } from '@/components/form-elements/NumberInput';
import useAppDispatch from '@/hooks/useAppDispatch';
import useAppSelector from '@/hooks/useAppSelector';
import useAuth from '@/hooks/useAuth';
import { useDownloadChallengesReport } from '@/hooks/useDownloadChallengesReport';
import {
  updateUserChallengeStep,
  updateUserTeamName,
} from '@/redux/actions/challenge';
import {
  getChallengesLoading,
  getHasFetchedUserChallenge,
  getUserChallengeSelector,
} from '@/redux/selectors/challengeSelectors';
import { IChallengeEntry } from '@/types/challenges';
import { Box, Button, CircularProgress } from '@mui/material';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';

type TFieldValue = string | number | null | undefined;

interface IUserChallengeFormValues {
  teamName: string | null;
  entries: IChallengeEntry[];
}

const SAVED_ICON_TIMEOUT_MS = 1000;

const UserChallenge: FC = () => {
  const dispatch = useAppDispatch();

  const { isTeamLead, user } = useAuth();

  const downloadChallengesReport = useDownloadChallengesReport();

  const userChallenge = useAppSelector(getUserChallengeSelector);
  const challengesLoading = useAppSelector(getChallengesLoading);
  const hasFetchedUserChallenge = useAppSelector(getHasFetchedUserChallenge);

  const [values, setValues] = useState<IUserChallengeFormValues | null>(null);

  const {
    status: fieldSaveStatus,
    errorMessage: fieldErrorMessage,
    markSaving,
    markSaved,
    markError,
  } = useFieldSaveStatus({ savedStatusTimeoutMs: SAVED_ICON_TIMEOUT_MS });

  // Guards markSaved/markError against out-of-order responses: a blur that
  // fires a save before the previous one for the same field has resolved
  // must not let the earlier response's outcome overwrite the later one's.
  const saveRequestId = useRef<Record<string, number>>({});
  const fieldValueOnFocus = useRef<Record<string, TFieldValue>>({});
  const hasLoadedInitialValues = useRef(false);

  const handleTeamNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValues((prev) =>
      prev ? { ...prev, teamName: event.target.value || null } : prev
    );
  };

  const getEntryValue = (index: number) =>
    values?.entries.find((entry) => entry.index === index)?.value;

  const handleEntryChange = (index: number, value: string) => {
    setValues((prev) => {
      if (!prev) {
        return prev;
      }

      if (value === '') {
        return {
          ...prev,
          entries: prev.entries.filter((entry) => entry.index !== index),
        };
      }

      const numericValue = Number(value);
      const exists = prev.entries.some((entry) => entry.index === index);
      const entries = exists
        ? prev.entries.map((entry) =>
            entry.index === index ? { ...entry, value: numericValue } : entry
          )
        : [...prev.entries, { index, value: numericValue }];

      return { ...prev, entries };
    });
  };

  const handleFocus = (field: string, value: TFieldValue) => {
    fieldValueOnFocus.current[field] = value;
  };

  const handleTeamNameBlur = () => {
    const field = 'teamName';
    // Trim before comparing/saving — a change that's only whitespace (e.g.
    // trailing spaces) isn't a real edit and shouldn't trigger a save. Both
    // sides of the comparison are trimmed the same way, so a stray space in
    // the value the field started with (from focus) doesn't get mistaken
    // for a real edit against the freshly-trimmed current value.
    const trimmedValue = values?.teamName?.trim() || null;
    const focusValueTrimmed =
      (fieldValueOnFocus.current[field] as string | null)?.trim() || null;

    if (!values || !userChallenge) {
      return;
    }

    // A prior failed save leaves the field out of sync with the server, so
    // a retry must fire even if nothing changed since the field was focused.
    const hadSaveError = fieldSaveStatus[field] === 'error';

    if (!hadSaveError && focusValueTrimmed === trimmedValue) {
      return;
    }

    setValues((prev) => (prev ? { ...prev, teamName: trimmedValue } : prev));

    // Belt-and-suspenders: skip the request if the trimmed value matches
    // what's already saved, regardless of what the focus snapshot said.
    if (!hadSaveError && trimmedValue === (userChallenge.teamName ?? null)) {
      return;
    }

    markSaving(field);

    const requestId = (saveRequestId.current[field] ?? 0) + 1;
    saveRequestId.current[field] = requestId;

    dispatch(
      updateUserTeamName({
        companiesChallengesId: userChallenge.companiesChallengesId,
        teamName: trimmedValue ?? '',
      })
    )
      .unwrap()
      .then(() => {
        if (saveRequestId.current[field] === requestId) {
          markSaved(field);
        }
      })
      .catch((err) => {
        if (saveRequestId.current[field] === requestId) {
          markError(field, err?.error ?? 'Failed to save');
        }
      });
  };

  const handleEntryBlur = (index: number) => {
    const field = `entry-${index}`;
    const value = getEntryValue(index);

    if (!values || !userChallenge) {
      return;
    }

    // A cleared field or a literal 0 are both "no answer" — send 0 (backend
    // now accepts it) and normalize the field back to empty locally,
    // matching how a 0 arrives from the backend.
    const valueToSave = value ?? 0;

    if (valueToSave === 0) {
      setValues((prev) =>
        prev
          ? {
              ...prev,
              entries: prev.entries.filter((entry) => entry.index !== index),
            }
          : prev
      );
    }

    // Compare on the same "no answer" normalization as above — otherwise
    // an empty field (focus value `undefined`) that gets typed into as "0"
    // and blurred looks like a change (`undefined !== 0`) every single
    // time, even though both mean "no answer" and nothing actually
    // changed on the backend, firing a request on every focus/blur cycle.
    // A prior failed save leaves the field out of sync with the server, so
    // a retry must fire even if nothing changed since the field was focused.
    const hadSaveError = fieldSaveStatus[field] === 'error';
    const focusValueToCompare = fieldValueOnFocus.current[field] ?? 0;
    if (!hadSaveError && focusValueToCompare === valueToSave) {
      return;
    }

    markSaving(field);

    const requestId = (saveRequestId.current[field] ?? 0) + 1;
    saveRequestId.current[field] = requestId;

    dispatch(
      updateUserChallengeStep({
        companiesChallengesId: userChallenge.companiesChallengesId,
        index,
        value: valueToSave,
      })
    )
      .unwrap()
      .then(() => {
        if (saveRequestId.current[field] === requestId) {
          markSaved(field);
        }
      })
      .catch((err) => {
        if (saveRequestId.current[field] === requestId) {
          markError(field, err?.error ?? 'Failed to save');
        }
      });
  };

  const handleDownloadReport = () => {
    const company = user?.companies[0];
    if (!company) {
      return;
    }

    downloadChallengesReport(company.id, company.name);
  };

  useEffect(() => {
    if (!userChallenge || hasLoadedInitialValues.current) {
      return;
    }

    hasLoadedInitialValues.current = true;

    setValues({
      teamName: userChallenge.teamName,
      entries: userChallenge.labels.filter((entry) => entry.value !== 0),
    });
  }, [userChallenge]);

  if (challengesLoading || !hasFetchedUserChallenge) {
    return (
      <ChallengeLoading>
        <CircularProgress />
      </ChallengeLoading>
    );
  }

  if (!userChallenge?.companiesChallenges?.id) {
    return <Navigate to="/edit-profile/info" replace />;
  }

  if (!values) {
    return null;
  }

  const { teamName } = values;
  const { title, description, labels } = userChallenge.companiesChallenges;

  const total = labels.reduce(
    (sum, label) => sum + (getEntryValue(label.index) ?? 0),
    0
  );

  return (
    <ChallengeContainer>
      <ChallengeHeaderRow>
        <FlexChallengeTitle variant="h2">{title}</FlexChallengeTitle>
        {isTeamLead && (
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
          >
            Challenges Report
          </Button>
        )}
      </ChallengeHeaderRow>
      {description && (
        <ChallengeDescription>{description}</ChallengeDescription>
      )}

      <ChallengeSection>
        <ChallengeSectionLabel variant="h3">Team Name</ChallengeSectionLabel>
        <ChallengeField
          fullWidth
          placeholder="Enter your team name"
          value={teamName ?? ''}
          onChange={handleTeamNameChange}
          onFocus={() => handleFocus('teamName', teamName)}
          onBlur={handleTeamNameBlur}
          error={fieldSaveStatus.teamName === 'error'}
          endAdornment={getStatusAdornment(fieldSaveStatus.teamName)}
        />
        {fieldErrorMessage.teamName && (
          <ChallengeFieldError>
            {fieldErrorMessage.teamName}
          </ChallengeFieldError>
        )}
      </ChallengeSection>

      <ChallengeSection>
        <ChallengeSectionLabel variant="h3">Your Entries</ChallengeSectionLabel>
        <Box>
          {labels.map((label, displayIndex) => (
            <ChallengeEntryRow
              key={label.index}
              entry={label}
              displayIndex={displayIndex}
              value={getEntryValue(label.index) ?? ''}
              status={fieldSaveStatus[`entry-${label.index}`]}
              errorMessage={fieldErrorMessage[`entry-${label.index}`]}
              onChange={(value) => handleEntryChange(label.index, value)}
              onFocus={() =>
                handleFocus(`entry-${label.index}`, getEntryValue(label.index))
              }
              onBlur={() => handleEntryBlur(label.index)}
            />
          ))}
          <ChallengeTotalRow>
            <ChallengeRowTotalLabel>TOTAL</ChallengeRowTotalLabel>
            <ChallengeTotalField
              disabled
              value={total}
              inputComponent={NumberFormatCustom as any}
              inputProps={{
                allowNegative: false,
                decimalScale: 0,
                readOnly: true,
              }}
            />
          </ChallengeTotalRow>
        </Box>
      </ChallengeSection>
    </ChallengeContainer>
  );
};

export default UserChallenge;
